import { useEffect, useState } from "react";
import { adminApi, extractApiError } from "../../lib/adminApi";
import {
    AdminPageHeader,
    SectionCard,
    FormActions,
} from "../../components/admin/FormPrimitives";
import { BilingualField } from "../../components/admin/BilingualField";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

function FaqCard({ faq, onSaved, onDeleted, onReorder }) {
    const [form, setForm] = useState({
        question: faq.question,
        answer: faq.answer,
        order: faq.order,
    });
    const [state, setState] = useState({ status: "idle", error: "" });

    const onSubmit = async (e) => {
        e.preventDefault();
        setState({ status: "submitting", error: "" });
        try {
            const { data } = await adminApi.put(`/admin/faqs/${faq.id}`, form);
            setState({ status: "success", error: "" });
            onSaved(data);
            setTimeout(
                () =>
                    setState((s) =>
                        s.status === "success"
                            ? { status: "idle", error: "" }
                            : s,
                    ),
                2500,
            );
        } catch (err) {
            setState({ status: "error", error: extractApiError(err) });
        }
    };

    const onDelete = async () => {
        if (!window.confirm("Supprimer cette question ?")) return;
        try {
            await adminApi.delete(`/admin/faqs/${faq.id}`);
            onDeleted(faq.id);
        } catch (err) {
            setState({
                status: "error",
                error: extractApiError(err, "Suppression impossible."),
            });
        }
    };

    return (
        <SectionCard
            title={`Question ${faq.order}`}
            testId={`faq-card-${faq.id}`}
        >
            <form onSubmit={onSubmit}>
                <BilingualField
                    label="Question"
                    idPrefix={`faq-q-${faq.id}`}
                    value={form.question}
                    onChange={(v) => setForm({ ...form, question: v })}
                />
                <BilingualField
                    label="Réponse"
                    type="textarea"
                    rows={3}
                    idPrefix={`faq-a-${faq.id}`}
                    value={form.answer}
                    onChange={(v) => setForm({ ...form, answer: v })}
                />
                <div className="flex items-center gap-3 flex-wrap">
                    <FormActions
                        saving={state.status === "submitting"}
                        status={state.status}
                        error={state.error}
                        testId={`faq-form-${faq.id}`}
                    />
                    <div className="flex items-center gap-2 ml-auto">
                        <button
                            type="button"
                            onClick={() => onReorder(faq.id, "up")}
                            className="p-2 border border-ink-border hover:border-gold hover:text-gold text-neutral-400 transition-colors"
                            aria-label="Monter"
                            data-testid={`faq-up-${faq.id}`}
                        >
                            <ArrowUp size={14} />
                        </button>
                        <button
                            type="button"
                            onClick={() => onReorder(faq.id, "down")}
                            className="p-2 border border-ink-border hover:border-gold hover:text-gold text-neutral-400 transition-colors"
                            aria-label="Descendre"
                            data-testid={`faq-down-${faq.id}`}
                        >
                            <ArrowDown size={14} />
                        </button>
                        <button
                            type="button"
                            onClick={onDelete}
                            className="p-2 border border-ink-border hover:border-red-500 hover:text-red-400 text-neutral-400 transition-colors"
                            aria-label="Supprimer"
                            data-testid={`faq-delete-${faq.id}`}
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>
            </form>
        </SectionCard>
    );
}

export default function AdminFaqs() {
    const [faqs, setFaqs] = useState([]);
    const [addState, setAddState] = useState({ status: "idle", error: "" });

    const reload = async () => {
        const { data } = await adminApi.get("/admin/faqs");
        setFaqs((data || []).sort((a, b) => a.order - b.order));
    };

    useEffect(() => {
        reload();
    }, []);

    const onCreate = async () => {
        setAddState({ status: "submitting", error: "" });
        try {
            await adminApi.post("/admin/faqs", {
                question: { fr: "Nouvelle question", en: "New question" },
                answer: { fr: "Réponse à compléter…", en: "Answer to complete…" },
            });
            await reload();
            setAddState({ status: "idle", error: "" });
        } catch (err) {
            setAddState({ status: "error", error: extractApiError(err) });
        }
    };

    const swapOrder = async (id, direction) => {
        const idx = faqs.findIndex((f) => f.id === id);
        if (idx < 0) return;
        const swap = direction === "up" ? idx - 1 : idx + 1;
        if (swap < 0 || swap >= faqs.length) return;
        const a = faqs[idx];
        const b = faqs[swap];
        try {
            await Promise.all([
                adminApi.put(`/admin/faqs/${a.id}`, { order: b.order }),
                adminApi.put(`/admin/faqs/${b.id}`, { order: a.order }),
            ]);
            await reload();
        } catch {
            /* ignore */
        }
    };

    return (
        <div data-testid="admin-faqs-page">
            <AdminPageHeader
                title="FAQ — Questions fréquentes"
                subtitle="Ajouter, modifier, réordonner ou supprimer les questions publiées sur la page Fonctionnement."
            />
            <div className="mb-6">
                <button
                    type="button"
                    onClick={onCreate}
                    disabled={addState.status === "submitting"}
                    className="btn-gold"
                    data-testid="faq-add-btn"
                >
                    <Plus size={14} /> Ajouter une question
                </button>
                {addState.status === "error" && (
                    <div className="mt-3 text-sm text-red-300">
                        {addState.error}
                    </div>
                )}
            </div>
            {faqs.map((f) => (
                <FaqCard
                    key={f.id}
                    faq={f}
                    onSaved={reload}
                    onDeleted={reload}
                    onReorder={swapOrder}
                />
            ))}
        </div>
    );
}
